import { isNil, orderBy, uniqBy } from 'lodash';
import { DateTime } from 'luxon';
import { FunctionComponent, useCallback, useEffect, useId, useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Button, Col, Container, Row } from 'reactstrap';
import { gql, useMutation, useQuery } from 'urql';
import { DeepPartialGraph, GraphType, Override } from '../types';
import { HacpackMutation, HacpackMutationSendDataArguments, HacpackQuery, HacpackQueryTerminalArguments, Handling, HandlingType, Result, ShipArrival } from '../types/graph';
import Loading from './Loading';
import { GridLayout } from './ui';

// @ts-ignore
type TerminalSendDataQueryData = DeepPartialGraph<HacpackQuery>
type TerminalSendDataQueryArguments = HacpackQueryTerminalArguments;

const terminalSegregationQuery = gql<TerminalSendDataQueryData, TerminalSendDataQueryArguments>`
query terminal($id: Int!) {
    terminal (id: $id) {
        id
        name
        slug
        port
        mailingListsForTerminalsDataSend {
            id
            description
            emails
        }
        handlings(types:[${HandlingType.Boarding},${HandlingType.TranshipmentBoarding},${HandlingType.Unboarding},${HandlingType.TranshipmentUnboarding},${HandlingType.Transit}]) {
            id
            loadingUnitIdentifier
            checkedIn
            checkedOut
            type
            shipArrival {
                uid
                ship
                eta
                etd
            }
            dangerousGoods {
                id
                adrClass
            }
        }
    }
}`;

type SendDataMutationData = GraphType<HacpackMutation> & {
    sendData: Override<Result, { data: boolean }>
};

type SendDataMutationArguments = HacpackMutationSendDataArguments;


const sendDataMutation = gql<SendDataMutationData, SendDataMutationArguments>`mutation ($mailingListsIds: [Int!], $handlingIds: [Long!]!, $additionalAddresses: [String!], $message: String) {
    sendData (mailingListsIds: $mailingListsIds, handlingIds: $handlingIds, additionalAddresses: $additionalAddresses, message: $message)  {
        isOk
    }
}`;

type TerminalSendDataProps = {
    id: number
}

type TerminalSendDataFormValues = {
    mailingListsIds: string[],
    terminalName: string,
    imports: boolean,
    exports: boolean,
    transits: boolean,
    shipArrivalId: string,
    shipArrivalUid: string,
    additionalAddresses: string,
    message: string
}

const TerminalSendData: FunctionComponent<TerminalSendDataProps> = ({ id }) => {

    const [result, reexecuteQuery] = useQuery<TerminalSendDataQueryData, TerminalSendDataQueryArguments>({
        query: terminalSegregationQuery,
        variables: { id },
        requestPolicy: 'cache-and-network'
    });

    const [sendDataResult, sendData] = useMutation(sendDataMutation);

    const { fetching: sending, error, data: sendResultData } = sendDataResult;

    useEffect(() => {

    }, [sendResultData, error]);

    //const [exports, setExports] = useState(true);
    //const [imports, setImports] = useState(true);
    //const [transits, setTransits] = useState(true);
    //const [inYard, setInYard] = useState(false);

    const uid = useId();

    const { register, handleSubmit, formState, getValues } = useForm<TerminalSendDataFormValues>({
        defaultValues: {
            terminalName: "",
            mailingListsIds: [],
            imports: true,
            exports: true,
            transits: true,
            shipArrivalId: "",
            shipArrivalUid: "",
            additionalAddresses: "",
            message: ""
        }
    });

    const { data, fetching, /*error*/ } = result;

    const terminal = data?.terminal ?? null;

    const imports = getValues('imports')
    const exports = getValues('exports')
    const transits = getValues('transits')

    const { handlings, transitsCount, exportsCount, importsCount } = useMemo(() => {

        const handlings: Handling[] = [];
        const arrivalsById = new Map<string, ShipArrival>()
        let transitsCount = 0;
        let exportsCount = 0;
        let importsCount = 0;
        let inYardCount = 0;

        const l = terminal?.handlings?.length ?? 0;

        for (let i = 0; i < l; i++) {
            console.log({ handlings, transitsCount, exportsCount, importsCount, inYardCount });

            const handling = terminal!.handlings![i];
            const arrival = handling.shipArrival;

            if (isNil(arrival)) {
                continue;
            }


                switch (handling.type) {
                    case HandlingType.Transit:
                        transitsCount++;
                        if (transits) {
                            break;
                        }
                        continue;
                    case HandlingType.Boarding:
                    case HandlingType.TranshipmentBoarding:
                        exportsCount++;
                        if (exports) {
                            break;
                        }
                        continue;
                    case HandlingType.Unboarding:
                    case HandlingType.TranshipmentUnboarding:
                        importsCount++;
                        if (imports) {
                            break;
                        }
                        continue;
                    default:
                        continue;
                }

                handlings.push(handling);

            handlings.push(handling);
        }

        return { handlings, transitsCount, exportsCount, importsCount };
    }, [terminal?.handlings, imports, exports]);

    const onSubmit: SubmitHandler<TerminalSendDataFormValues> = useCallback((data) => {
        const { shipArrivalUid, message } = data;

        const handlingIds = handlings.filter(handling => handling.shipArrival?.uid === shipArrivalUid).map(handling => handling.id!);
        const mailingListsIds = data.mailingListsIds.map(Number);
        const additionalAdresses = data.additionalAddresses.split(',')

        sendData({ handlingIds, mailingListsIds, additionalAddresses: additionalAdresses, message });
    }, [handlings, sendData]);

    const arrivals = useMemo(() => uniqBy(handlings.map(handling => handling.shipArrival).filter((shipArrival): shipArrival is ShipArrival => !isNil(shipArrival)), 'uid'), [handlings]);

    const arrivalsSelect = useMemo(() => {
        const id = `${uid}shipArrivalUid`

        return <div className="mb-3">
            <label htmlFor={id}>Viaggio Nave</label>
            <select className="form-select" id={id} {...register('shipArrivalUid', { required: true })}>
                <option value="">--seleziona arrivo nave--</option>
                {orderBy(arrivals, ['ship', 'eta'], ['asc', 'asc']).map((arrival) => {
                    const ship = arrival.ship!;
                    const eta = DateTime.fromISO(arrival.eta!).setLocale('it').toLocaleString(DateTime.DATETIME_SHORT);
                    const etd = DateTime.fromISO(arrival.etd!).setLocale('it').toLocaleString(DateTime.DATETIME_SHORT);

                    return <option key={arrival.uid} value={arrival.uid}>{`${ship}; ETA ${eta}; ETD ${etd}`}</option>
                })}
            </select>
        </div>
    }, [register, arrivals, uid]);

    const mailingLists = terminal?.mailingListsForTerminalsDataSend ?? null;

    const mailingListsInputs = useMemo(() => {
        if (mailingLists === null) {
            return null;
        }
        return <div className="mb-3">{
            mailingLists.map((mailingList, i) => {
                const id = `${uid}mailingList${mailingList.id}`

                return <div key={mailingList.id} className="form-check">
                    <input {...register('mailingListsIds')} className="form-check-input" type="checkbox" value={mailingList.id} id={id} />
                    <label className="form-check-label" htmlFor={id}>{mailingList.description}</label>
                </div>
            })
        }</div>;
    }, [register, mailingLists, uid]);

    if (fetching) {
        return <GridLayout><Loading /></GridLayout>
    }

    return <form onSubmit={handleSubmit(onSubmit)} className="pt-2">
        <Container>
            <Row>
                <Col md="3">
                    <fieldset>
                        <legend>Liste d'invio</legend>
                        {mailingListsInputs}
                    </fieldset>
                </Col>

                <Col md="9">
                    {/*<div className="p-2">*/}
                    {/*    <div className="form-check">*/}
                    {/*        <input className="form-check-input" disabled={exportsCount === 0} type="checkbox" checked={exports} onChange={(ev) => setExports(ev.target.checked)} id={`${uid}exportsCheckbox`} />*/}
                    {/*        <label className="form-check-label" htmlFor={`${uid}exportsCheckbox`}>Imbarchi</label>*/}
                    {/*    </div>*/}

                    {/*    <div className="form-check">*/}
                    {/*        <input className="form-check-input" disabled={importsCount === 0} type="checkbox" checked={imports} onChange={(ev) => setImports(ev.target.checked)} id={`${uid}importsCheckbox`} />*/}
                    {/*        <label className="form-check-label" htmlFor={`${uid}importsCheckbox`}>Sbarchi</label>*/}
                    {/*    </div>*/}

                    {/*    <div className="form-check">*/}
                    {/*        <input className="form-check-input" disabled={transitsCount === 0} type="checkbox" checked={transits} onChange={(ev) => setTransits(ev.target.checked)} id={`${uid}transitsCheckbox`} />*/}
                    {/*        <label className="form-check-label" htmlFor={`${uid}transitsCheckbox`}>Transiti</label>*/}
                    {/*    </div>*/}

                    {/*    <div className="form-check">*/}
                    {/*        <input className="form-check-input" disabled={inYardCount === 0} type="checkbox" checked={inYard} onChange={(ev) => setInYard(ev.target.checked)} id={`${uid}inYardCheckbox`} />*/}
                    {/*        <label className="form-check-label" htmlFor={`${uid}inYardCheckbox`}>A piazzale</label>*/}
                    {/*    </div>*/}
                    {/*</div>*/}

                    <div className="p-2">
                        <div className="form-check">
                            <input {...register('exports')} className="form-check-input" type="checkbox" id={`${uid}exports`} />
                            <label className="form-check-label" htmlFor={`${uid}exports`}>Imbarchi</label>
                        </div>

                        <div className="form-check">
                            <input {...register('imports')} className="form-check-input" type="checkbox" id={`${uid}imports`} />
                            <label className="form-check-label" htmlFor={`${uid}imports`}>Sbarchi</label>
                        </div>

                        <div className="form-check">
                            <input {...register('transits')} className="form-check-input" type="checkbox" id={`${uid}transits`} />
                            <label className="form-check-label" htmlFor={`${uid}transits`}>Transiti</label>
                        </div>
                    </div>

                    {arrivalsSelect}

                    <div className="mb-3">
                        <label htmlFor={`${uid}additionalAddresses`}>Indirizzi aggiuntivi</label>
                        <input {...register('additionalAddresses')} className="form-control" id={`${uid}additionalAddresses`} type="email" multiple />
                    </div>

                    <div className="mb-3">
                        <label htmlFor={`${uid}additionalAdresses`}>Messaggio</label>
                        <textarea {...register('message')} className="form-control" id={`${uid}message`} />
                    </div>

                    <Button type="submit">Invia</Button>
                </Col>
            </Row>
        </Container>
        {/*<pre>{JSON.stringify(formState, null, 2)}</pre>*/}
    </form>;
};

export default TerminalSendData;